import { useGetSegmentUsers } from 'api/CampaignAnalyticsAPI';
import { useAvailableLookupTypes } from 'modules/contactLookup/hooks/useAvailableLookupTypes';
import { LookupType } from 'modules/contactLookup/types';
import { DEFAULT_SORT_DIRECTION } from 'modules/quickActionsMenu/constants';
import { useQuickActionsMenuContext } from 'modules/quickActionsMenu/contexts';
import { useEffect, useMemo } from 'react';
import { ApiError } from 'services/api';
import { useRumError } from 'services/observability/datadog';
import { Product, Team } from 'teams';

const createQuery = (identifier: string, debouncedSearchTerm: string) => {
  return {
    page: 1,
    pageSize: debouncedSearchTerm.length < 1 ? 0 : 100,
    orderBy: identifier,
    sorting: DEFAULT_SORT_DIRECTION,
    searchQuery: {
      combinator: 'Or',
      searchQueries: [
        {
          combinator: 'Or',
          searchQueries: [
            {
              dataType: 'user',
              searchCombo: {
                combinator: 'And',
                searchQueries: [
                  {
                    field: identifier,
                    fieldType: 'string',
                    comparatorType: 'Contains',
                    dataType: 'user',
                    id: 1,
                    value: debouncedSearchTerm,
                  },
                ],
              },
            },
          ],
        },
      ],
    },
  };
};

export const useUserSearch = () => {
  const addErrorSignal = useRumError();
  const availableLookupTypes = useAvailableLookupTypes();
  const lookupType = availableLookupTypes[0];
  const { debouncedSearchTerm } = useQuickActionsMenuContext();
  const userIdentifier = lookupType === LookupType.ByUserId ? 'userId' : 'email';

  const query = useMemo(
    () => createQuery(userIdentifier, debouncedSearchTerm),
    [debouncedSearchTerm, userIdentifier],
  );

  const {
    data: { results = [] } = {},
    isLoading,
    mutate: queryUsers,
  } = useGetSegmentUsers({
    onError: (error: ApiError) => {
      addErrorSignal(error, 'QuickActionsMenu > useUserSearch', {
        name: 'Quick Actions Menu users REST query',
        productName: Product.CONTACT_LOOKUP,
        teamName: Team.AUDIENCE_TEAM,
      });
    },
  });
  const users = results.map((user: any) => user[userIdentifier]);

  useEffect(() => {
    // trigger the POST request when search value changes
    queryUsers(query);
  }, [query]);

  return { users, isLoading };
};
